// client/src/services/formsApi.js
// Interest / lead forms (Growth Phase 4, feature_key "forms").
// Staff calls go through services/http (session cookie); public calls are
// plain fetches that never send credentials and never bounce to /login.

import { delJson, getJson, patchJson, postJson } from './http';
import pushNotificationService from './pushNotificationService';

const BASE = (process.env.REACT_APP_BASE_URL || '').replace(/\/+$/, '');
const APP_ORIGIN = (process.env.REACT_APP_BASE_URL || window.location.origin || '').replace(/\/+$/, '');

// The t_registration_field_def vocabulary (server/services/formFields.js).
// rating (1-5) and nps (0-10) are the survey types (Growth Phase 9).
export const FIELD_TYPES = ['text', 'textarea', 'select', 'checkbox', 'date', 'phone', 'email', 'rating', 'nps'];
export const PURPOSES = ['notify', 'interest', 'rsvp', 'volunteer', 'preregister', 'other'];

// ---- staff ----
export const listForms = (tenant, { eventId } = {}) =>
  getJson(`/api/forms/${tenant}/forms${eventId ? `?eventId=${encodeURIComponent(eventId)}` : ''}`);
export const getForm = (tenant, id) => getJson(`/api/forms/${tenant}/forms/${id}`);
export const createForm = (tenant, body) => postJson(`/api/forms/${tenant}/forms`, body);
export const updateForm = (tenant, id, body) => patchJson(`/api/forms/${tenant}/forms/${id}`, body);
export const archiveForm = (tenant, id) => delJson(`/api/forms/${tenant}/forms/${id}`);
export const rotateWebhookSecret = (tenant, id) => postJson(`/api/forms/${tenant}/forms/${id}/webhook-secret`, {});
export const listSubmissions = (tenant, id, { limit = 50, offset = 0 } = {}) =>
  getJson(`/api/forms/${tenant}/forms/${id}/submissions?limit=${limit}&offset=${offset}`);
export const submissionsCsvUrl = (tenant, id) => `${BASE}/api/forms/${tenant}/forms/${id}/submissions?format=csv`;

// ---- embed / share URLs ----
export const hostedFormUrl = (tenantSlug, id) => `${APP_ORIGIN}/f/${encodeURIComponent(tenantSlug)}/${id}`;
export const embedFormUrl = (tenantSlug, id) => `${APP_ORIGIN}/embed/f/${encodeURIComponent(tenantSlug)}/${id}`;
export function iframeSnippet(tenantSlug, id, title = 'Form') {
  const safeTitle = String(title).replace(/"/g, '&quot;');
  // The script resizes the iframe to fit the form (Growth Phase 8, /embed/resize.js).
  return `<iframe src="${embedFormUrl(tenantSlug, id)}" title="${safeTitle}" width="100%" height="560" style="border:0;max-width:640px" loading="lazy"></iframe>\n<script src="${APP_ORIGIN}/embed/resize.js" async></script>`;
}

// ---- public ----
async function publicFetch(path, { method = 'GET', body } = {}) {
  const res = await fetch(`${BASE}${path}`, {
    method,
    credentials: 'omit',
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || data.message || `${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

const slugPath = (tenantSlug) => encodeURIComponent(tenantSlug);
export const fetchPublicForm = (tenantSlug, id) => publicFetch(`/api/forms/public/${slugPath(tenantSlug)}/${id}`);
export const fetchEventPageForms = (tenantSlug, eventId) =>
  publicFetch(`/api/forms/public/${slugPath(tenantSlug)}/event/${eventId}`);
export const submitPublicForm = (tenantSlug, id, body) =>
  publicFetch(`/api/forms/public/${slugPath(tenantSlug)}/${id}/submit`, { method: 'POST', body });

/** os_vid cookie (attribution), sent explicitly the way ticket checkout does. */
export function readVisitorId() {
  const m = (typeof document !== 'undefined' ? document.cookie : '').match(/(?:^|;\s*)os_vid=([a-f0-9]{32})/);
  return m ? m[1] : undefined;
}

export function browserSupportsPush() {
  return typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/**
 * Ask for notification permission, subscribe this browser with the platform
 * VAPID key, and hand the subscription to the form's push endpoint together
 * with the one-time token the submit returned. Resolves true on success.
 */
export async function enableFormPush(tenantSlug, id, token) {
  if (!browserSupportsPush()) throw new Error('unsupported');
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('denied');
  const registration = await Promise.race([
    navigator.serviceWorker.ready,
    new Promise((_, reject) => setTimeout(() => reject(new Error('no-service-worker')), 8000)),
  ]);
  const keyRes = await publicFetch('/api/notifications/push/vapid-public-key');
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: pushNotificationService.urlBase64ToUint8Array(keyRes.publicKey),
  });
  await publicFetch(`/api/forms/public/${slugPath(tenantSlug)}/${id}/push`, {
    method: 'POST',
    body: { token, subscription: subscription.toJSON() },
  });
  return true;
}
